/*
 * The small parts every screen is built from, with Atlantis names and props: Page, Button, StatusLabel, Card, Banner,
 * EmptyState, Avatar, Switch, Chip, details lists.
 */
import { Link } from "@tanstack/react-router";
import { personColor } from "@steward/shared";
import type { ButtonHTMLAttributes, ReactNode } from "react";
import { usePicture } from "../api/hooks";
import { Icon, type IconName } from "./Icon";

export function Page({
  title,
  titleMetaData,
  subtitle,
  intro,
  primaryAction,
  secondaryAction,
  moreActions,
  fill,
  className,
  children,
}: {
  title: ReactNode;
  titleMetaData?: ReactNode;
  subtitle?: ReactNode;
  intro?: ReactNode;
  primaryAction?: ReactNode;
  secondaryAction?: ReactNode;
  moreActions?: ReactNode;
  /** Fits the window like Jobber's schedule: the page never scrolls, only the parts inside it that must. */
  fill?: boolean;
  /** Extra class, such as pform for a form page (flush sections and a Save bar on phones). */
  className?: string;
  children: ReactNode;
}) {
  return (
    <div className={["page", fill ? "fill" : null, className].filter(Boolean).join(" ")}>
      <div className="page-head">
        <div className="page-title">
          <h1>
            {title}
            {titleMetaData}
          </h1>
          {subtitle ? <p className="subtitle">{subtitle}</p> : null}
        </div>
        {primaryAction || secondaryAction || moreActions ? (
          <div className="page-actions">
            {moreActions}
            {secondaryAction}
            {primaryAction}
          </div>
        ) : null}
      </div>
      {intro ? <p className="intro">{intro}</p> : null}
      {children}
    </div>
  );
}

type ButtonProps = {
  type?: "primary" | "secondary" | "tertiary";
  variation?: "work" | "subtle" | "destructive";
  size?: "small" | "base" | "large";
  fullWidth?: boolean;
  icon?: IconName;
  loading?: boolean;
  submit?: boolean;
  children: ReactNode;
} & Omit<ButtonHTMLAttributes<HTMLButtonElement>, "type">;

export function Button({
  type = "primary",
  variation = "work",
  size = "base",
  fullWidth,
  icon,
  loading,
  submit,
  children,
  ...rest
}: ButtonProps) {
  const cls = ["btn", type, variation === "work" ? "" : variation, size === "base" ? "" : size, fullWidth ? "full" : ""]
    .filter(Boolean)
    .join(" ");
  return (
    <button
      className={cls}
      type={submit ? "submit" : "button"}
      disabled={(loading ?? false) || (rest.disabled ?? false)}
      {...rest}
    >
      {loading ? <span className="spin" aria-hidden /> : icon ? <Icon name={icon} size={18} /> : null}
      {children}
    </button>
  );
}

export type Status = "success" | "warning" | "critical" | "informative" | "inactive";

export function StatusLabel({ status, children, pill }: { status: Status; children: ReactNode; pill?: boolean }) {
  return <span className={`status ${status}${pill ? " pill" : ""}`}>{children}</span>;
}

export function Card({
  id,
  title,
  action,
  flush,
  children,
}: {
  /** An anchor, so Settings search can open the page at this card. */
  id?: string;
  title?: ReactNode;
  action?: ReactNode;
  flush?: boolean;
  children: ReactNode;
}) {
  return (
    <section className="card" id={id}>
      {title || action ? (
        <div className="card-head">
          {title ? <h2>{title}</h2> : <span />}
          {action}
        </div>
      ) : null}
      <div className={`card-body${flush ? " flush" : ""}`}>{children}</div>
    </section>
  );
}

const BANNER_ICON: Record<string, IconName> = { notice: "info", success: "check", warning: "warning", error: "warning" };

export function Banner({
  type = "notice",
  children,
  action,
}: {
  type?: "notice" | "success" | "warning" | "error";
  children: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className={`banner ${type}`} role={type === "error" ? "alert" : "status"}>
      <Icon name={BANNER_ICON[type] ?? "info"} />
      <div className="text">{children}</div>
      {action}
    </div>
  );
}

export function EmptyState({
  icon,
  title,
  children,
  action,
  center,
}: {
  icon: IconName;
  title: string;
  children?: ReactNode;
  action?: ReactNode;
  center?: boolean;
}) {
  return (
    <div className={`empty${center ? " center" : ""}`}>
      <div className="art">
        <Icon name={icon} size={22} />
      </div>
      <h3>{title}</h3>
      {children ? <p>{children}</p> : null}
      {action}
    </div>
  );
}

/**
 * A person's circle: their initials on their color, or with a profile picture, the picture inside a thin ring of their
 * color (owner, 2026-10-30). person gives the color and picture (personColor picks one when they have none); without it
 * the name picks a color.
 */
export function Avatar({
  name,
  large,
  size = large ? "large" : "base",
  color,
  person,
  title,
}: {
  name: string;
  large?: boolean;
  size?: "small" | "base" | "large";
  color?: string | null;
  person?: { id: string; color?: string | null; photo?: string | null };
  title?: string;
}) {
  const picture = usePicture(person?.id, person?.photo).data;
  const initials = name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w.charAt(0).toUpperCase())
    .join("");
  const ring = person ? personColor(person) : (color ?? personColor({ id: name }));
  return (
    <span
      className={`avatar ${size}${picture ? " has-photo" : ""}`}
      style={{ ["--avatar" as string]: ring }}
      title={title}
      aria-hidden
    >
      {picture ? <img src={picture} alt="" /> : initials}
    </span>
  );
}

export function Switch({
  checked,
  onChange,
  label,
  disabled,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      role="switch"
      className="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange(!checked)}
    />
  );
}

export function Chip({
  pressed,
  onClick,
  children,
  count,
}: {
  pressed: boolean;
  onClick: () => void;
  children: ReactNode;
  count?: number;
}) {
  return (
    <button type="button" className="chip" aria-pressed={pressed} onClick={onClick}>
      {children}
      {count !== undefined ? <span className="count">{count}</span> : null}
    </button>
  );
}

export function Details({ rows }: { rows: [ReactNode, ReactNode][] }) {
  return (
    <dl className="details">
      {rows.map(([k, v], i) => (
        <div key={i}>
          <dt>{k}</dt>
          <dd>{v ?? <span className="muted">Not set</span>}</dd>
        </div>
      ))}
    </dl>
  );
}

/** One setting. changed marks a value that is not the app's default, so the few changed ones stand out. */
export function SettingRow({
  title,
  description,
  control,
  changed,
}: {
  title: ReactNode;
  description?: ReactNode;
  control: ReactNode;
  changed?: boolean;
}) {
  return (
    <div className="setting-row">
      <div className="what">
        <b>
          {title}
          {changed ? <em className="changed">Changed</em> : null}
        </b>
        {description ? <span>{description}</span> : null}
      </div>
      {control}
    </div>
  );
}

export function TextLink({ to, children }: { to: string; children: ReactNode }) {
  return <Link to={to}>{children}</Link>;
}

/** "3 days ago", "2 hrs ago", "Just now": how long since something happened, in words. */
export function ago(iso: string, now = Date.now()): string {
  const s = Math.max(0, Math.round((now - Date.parse(iso)) / 1000));
  if (s < 60) return "Just now";
  const m = Math.round(s / 60);
  if (m < 60) return `${m} min ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h} ${h === 1 ? "hr" : "hrs"} ago`;
  const d = Math.round(h / 24);
  if (d < 30) return `${d} ${d === 1 ? "day" : "days"} ago`;
  return new Date(iso).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
}

/** "(504) 900-9981" from "+15049009981"; anything else as typed. */
export function prettyPhone(p: string): string {
  const d = p.replace(/\D/g, "").replace(/^1(?=\d{10}$)/, "");
  return d.length === 10 ? `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}` : p;
}

export const ROLE_WORDS: Record<string, string> = {
  owner: "Owner",
  franchise_owner: "Franchise owner",
  manager: "Manager",
  dispatch: "Dispatcher",
  crew: "Crew",
};

/** A button with only an icon: its name shows as a tooltip on hover and focus, and is read out by screen readers. */
export function IconButton({
  icon,
  label,
  onClick,
  size = 20,
  ...rest
}: { icon: IconName; label: string; onClick?: () => void; size?: number } & Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  "onClick" | "type"
>) {
  return (
    <button type="button" className="iconbtn" aria-label={label} data-tip={label} onClick={onClick} {...rest}>
      <Icon name={icon} size={size} />
    </button>
  );
}

/** Grey bars where a card's rows will be, while it loads. */
export function Skeleton({ rows = 3 }: { rows?: number }) {
  return (
    <div className="skeleton-rows" aria-busy="true" aria-label="Loading">
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="skeleton" />
      ))}
    </div>
  );
}

/** What to show when a record could not load: the server's own words and a way to try again. */
export function LoadError({ error, retry }: { error: unknown; retry?: () => void }) {
  const text = error instanceof Error ? error.message : "That did not load. Try again.";
  return (
    <Banner
      type="error"
      action={
        retry ? (
          <Button type="secondary" size="small" onClick={retry}>
            Try again
          </Button>
        ) : null
      }
    >
      {text}
    </Banner>
  );
}

/** The plain screen while the app or a new tab gets ready: no brand colors yet, so nothing flashes the wrong one. */
export function Loading() {
  return (
    <div style={{ display: "grid", placeItems: "center", height: "100%" }} className="muted">
      Loading
    </div>
  );
}
